import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../config/firebase';
import PlatformAvatar from '../components/platform/PlatformAvatar';
import PlatformBadge from '../components/platform/PlatformBadge';
import PlatformButton from '../components/platform/PlatformButton';
import '../styles/platform-dashboard.css';

const getMessageStatus = (message) => {
  const status = message?.messageStatus || message?.status || 'Sent';
  const isPending = String(status).toLowerCase() === 'pending';
  return { status, isPending };
};

const formatMessageDate = (message) => {
  if (message?.sentOn?.seconds) {
    return new Date(message.sentOn.seconds * 1000).toLocaleDateString();
  }

  if (message?.createdAt?.seconds) {
    return new Date(message.createdAt.seconds * 1000).toLocaleDateString();
  }

  if (message?.date) {
    return message.date;
  }

  return '—';
};

const collectMessagesFromContacts = (contacts = []) => {
  const messages = [];

  contacts.forEach((contact) => {
    (contact.messageQueue || []).forEach((message, index) => {
      const { status, isPending } = getMessageStatus(message);

      messages.push({
        id: message.id || `${contact.uid || contact.id}-${index}`,
        subject: message.title || message.subject || message.messageTitle || 'No subject',
        contactName: contact.name || contact.companyName || 'Unknown contact',
        status,
        isPending,
        date: formatMessageDate(message),
      });
    });
  });

  return messages.sort((a, b) => {
    if (a.isPending !== b.isPending) {
      return a.isPending ? -1 : 1;
    }

    return 0;
  });
};

const getUserName = (user) =>
  user?.fullName
  || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null)
  || user?.displayName
  || user?.name
  || user?.email
  || 'User';

export default function UserMessagesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.id;
  const [user, setUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) {
      navigate('/dashboard/users');
      return;
    }

    const loadUserMessages = async () => {
      setLoading(true);
      setError('');

      try {
        const userDoc = await db.collection('users').doc(userId).get();
        if (userDoc.exists) {
          setUser({ id: userDoc.id, ...userDoc.data() });
        }

        const contactsSnapshot = await db
          .collection('contacts')
          .where('contacterId', '==', userId)
          .get();

        const contactList = contactsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setContacts(contactList);
      } catch (loadError) {
        console.log('Error loading user messages:', loadError);
        setError('Could not load messages for this user.');
      } finally {
        setLoading(false);
      }
    };

    loadUserMessages();
  }, [userId, navigate]);

  const allMessages = useMemo(() => collectMessagesFromContacts(contacts), [contacts]);

  const filteredMessages = useMemo(() => {
    if (filter === 'pending') {
      return allMessages.filter((message) => message.isPending);
    }

    if (filter === 'sent') {
      return allMessages.filter((message) => !message.isPending);
    }

    return allMessages;
  }, [allMessages, filter]);

  const pendingCount = allMessages.filter((message) => message.isPending).length;
  const sentCount = allMessages.length - pendingCount;
  const userName = getUserName(user);

  const tabs = [
    ['all', `All (${allMessages.length})`],
    ['pending', `Pending (${pendingCount})`],
    ['sent', `Sent (${sentCount})`],
  ];

  return (
    <div className="nurturer-platform">
      <div className="platform-page platform-fade">
        <div className="platform-row users-toolbar" style={{ marginBottom: 8 }}>
          <PlatformButton variant="link" icon="chevL" onClick={() => navigate('/dashboard/users')}>
            Back to users
          </PlatformButton>
        </div>

        <div className="platform-card" style={{ marginBottom: 16, padding: '18px 20px' }}>
          <div className="platform-row" style={{ gap: 16 }}>
            <PlatformAvatar name={userName} size={56} photoUrl={user?.photoUrl} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>{userName}</div>
              <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 4 }}>
                {user?.email || '—'}
              </div>
            </div>
          </div>
        </div>

        {error && <div className="platform-alert platform-alert--error">{error}</div>}

        <div className="platform-row settings-tabs">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`settings-tab ${filter === id ? 'active' : ''}`}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="platform-card users-loading">Loading messages…</div>
        ) : (
          <div className="platform-card users-table-card">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th className="hide-mobile">Contact</th>
                  <th>Status</th>
                  <th className="hide-mobile">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredMessages.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: 'var(--muted)', padding: '32px 18px' }}>
                      No {filter === 'all' ? '' : `${filter} `}messages found for this user.
                    </td>
                  </tr>
                ) : (
                  filteredMessages.map((message) => (
                    <tr key={message.id} className="users-table-row">
                      <td style={{ fontWeight: 600 }}>{message.subject}</td>
                      <td className="hide-mobile">{message.contactName}</td>
                      <td>
                        <PlatformBadge tone={message.isPending ? 'pending' : 'sent'} dot={message.isPending}>
                          {message.status}
                        </PlatformBadge>
                      </td>
                      <td className="hide-mobile" style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--muted)' }}>
                        {message.date}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
